import { useEffect, useState } from "react";
import { Trash2, Save, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import {
  adminListFestivalMuhurtas, adminCreateFestivalMuhurta,
  adminUpdateFestivalMuhurta, adminDeleteFestivalMuhurta,
} from "@/lib/api";

const BLANK = { slug: "", name: "", name_hi: "", date: "", deity: "", significance: "", significance_hi: "", enabled: true, order: 0 };
const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

export default function AdminFestivalMuhurta() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState(null);

  const load = () => adminListFestivalMuhurtas().then((d) => setList(d.festivals || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Festival name is required");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) return toast.error("Date must be YYYY-MM-DD");
    const payload = {
      ...form,
      order: editing ? (parseInt(form.order, 10) || 0) : (list.reduce((mx, f) => Math.max(mx, f.order || 0), 0) + 1),
    };
    try {
      if (editing) { await adminUpdateFestivalMuhurta(editing, payload); toast.success("Festival updated"); }
      else { await adminCreateFestivalMuhurta(payload); toast.success("Festival added — city muhurta pages are now live & indexed"); }
      setForm(BLANK); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed — are you logged in as admin?"); }
  };

  const edit = (f) => {
    setEditing(f.id);
    setForm({ slug: f.slug || "", name: f.name || "", name_hi: f.name_hi || "", date: f.date || "", deity: f.deity || "", significance: f.significance || "", significance_hi: f.significance_hi || "", enabled: f.enabled !== false, order: f.order ?? 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const toggle = async (f) => { await adminUpdateFestivalMuhurta(f.id, { enabled: f.enabled === false }); load(); };
  const del = async (f) => { if (!window.confirm(`Delete ${f.name}? Its city muhurta pages will be removed.`)) return; await adminDeleteFestivalMuhurta(f.id); load(); };

  const F = (k, ph, type = "text") => (
    <input type={type} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} data-testid={`fm-${k}`} className={inp} />
  );

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="admin-festival-muhurta">
      <h1 className="mb-1 text-2xl font-semibold text-ink">Festival Muhurta Pages</h1>
      <p className="mb-6 text-sm text-ink-3">
        {list.length} festivals. Each enabled festival publishes a public, SEO-indexed muhurta page for <b>every City Panchangam city</b> at
        <code className="mx-1 rounded bg-surface-alt px-1.5 py-0.5 text-ink-2">/muhurta/&lt;festival&gt;/&lt;city&gt;</code>
        (e.g. <code className="rounded bg-surface-alt px-1.5 py-0.5 text-ink-2">/muhurta/diwali/new-york</code>), computed live in each city's timezone. Pages are added to the sitemap automatically.
      </p>

      <div className="mb-8 space-y-3 border border-line bg-surface p-5">
        <h2 className="font-medium text-ink">{editing ? "Edit festival" : "Add a festival"}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {F("name", "Festival name e.g. Diwali (Lakshmi Puja)")}
          {F("slug", "URL slug e.g. diwali (auto from name if blank)")}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {F("name_hi", "Hindi name e.g. दीपावली")}
          {F("date", "Date", "date")}
          {F("deity", "Deity e.g. Lakshmi")}
        </div>
        <textarea value={form.significance} onChange={(e) => setForm((f) => ({ ...f, significance: e.target.value }))} placeholder="Significance (English)" data-testid="fm-significance" className={inp} rows={2} />
        <textarea value={form.significance_hi} onChange={(e) => setForm((f) => ({ ...f, significance_hi: e.target.value }))} placeholder="Significance (Hindi)" data-testid="fm-significance_hi" className={inp} rows={2} />
        <label className="flex items-center gap-2 text-sm text-ink-2">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} data-testid="fm-enabled" />
          Enabled (public & in sitemap)
        </label>
        <div className="flex gap-2">
          <button onClick={save} data-testid="fm-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white hover:bg-[var(--saffron-dark)]"><Save className="h-4 w-4" />{editing ? "Update festival" : "Add festival"}</button>
          {editing && <button onClick={() => { setForm(BLANK); setEditing(null); }} className="border border-line px-4 py-2 text-sm text-ink-2 hover:text-ink">Cancel</button>}
        </div>
      </div>

      <div className="space-y-2">
        {list.map((f) => (
          <div key={f.id} data-testid={`admin-fm-${f.slug}`} className="flex items-center justify-between gap-3 border border-line bg-surface p-3">
            <div className="min-w-0">
              <p className="font-medium text-ink">
                {f.name}
                {f.enabled === false && <span className="ml-2 rounded bg-surface-alt px-1.5 py-0.5 text-[10px] uppercase text-ink-3">disabled</span>}
              </p>
              <p className="text-xs text-ink-3">{f.date} · {f.deity || "—"} · /muhurta/{f.slug}</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <a href={`/muhurta/${f.slug}`} target="_blank" rel="noopener noreferrer" className="border border-line px-2 py-1 text-xs text-ink-2 hover:text-ink" title="View"><ExternalLink className="h-3.5 w-3.5" /></a>
              <button onClick={() => toggle(f)} className="border border-line px-2 py-1 text-xs text-ink-2 hover:text-ink" title={f.enabled === false ? "Enable" : "Disable"} data-testid={`fm-toggle-${f.slug}`}>{f.enabled === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
              <button onClick={() => edit(f)} className="border border-line px-2 py-1 text-xs text-ink-2 hover:text-ink" data-testid={`fm-edit-${f.slug}`}>Edit</button>
              <button onClick={() => del(f)} data-testid={`fm-del-${f.slug}`} className="border border-red-300 px-2 py-1 text-xs text-red-500 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
